<template>
  <div class="mask" @mousedown.self="onCancel" @dragover.prevent @drop.prevent>
    <div class="import-dialog">
      <div class="import-header">
        <span class="import-header-title">{{ $t("homePage.import.dialogTitle") }}</span>
        <button type="button" class="import-close-button" @click="onCancel">
          <IconClose class="import-close-icon" />
        </button>
      </div>

      <div
        class="dropzone"
        :data-dragover="isDragOver ? 'true' : 'false'"
        :data-error="errorMessage ? 'true' : 'false'"
        @click.self="onDropzoneClick"
        @dragenter.prevent="onDragEnter"
        @dragover.prevent
        @dragleave.prevent="onDragLeave"
        @drop.prevent="onDrop"
      >
        <input
          ref="folderInput"
          type="file"
          class="dropzone-input"
          webkitdirectory
          tabindex="-1"
          aria-hidden="true"
          @change="onFolderInputChange"
        />
        <input
          ref="fileInput"
          type="file"
          class="dropzone-input"
          accept=".txt,.docx"
          multiple
          tabindex="-1"
          aria-hidden="true"
          @change="onFileInputChange"
        />

        <div v-if="isBusy" class="dropzone-placeholder">
          <span class="dropzone-label">{{ $t("homePage.import.loading") }}</span>
        </div>
        <div v-else-if="!hasSelection" class="dropzone-placeholder is-clickable" @click="selectFile">
          <IconUpload class="dropzone-icon" />
          <i18n path="homePage.import.dropzoneLabel" tag="p" class="dropzone-label">
            <span place="action" class="import-trigger-wrap">
              <button
                type="button"
                class="import-trigger"
                aria-haspopup="menu"
                :aria-expanded="isMenuOpen ? 'true' : 'false'"
                @click.stop="toggleMenu"
                @keydown.esc="closeMenu"
              >
                {{ $t("homePage.import.dropzoneLabelAction") }}
                <IconChevronDown class="import-trigger-icon" />
              </button>
              <div v-if="isMenuOpen" class="import-menu" @click.stop @keydown.esc="closeMenu">
                <button type="button" ref="firstMenuItem" class="import-menu-item" @click.stop="selectFolder">
                  <IconFolder class="import-menu-item-icon" />
                  <span>{{ $t("homePage.import.selectFolder") }}</span>
                </button>
                <button type="button" class="import-menu-item" @click.stop="selectFile">
                  <IconFile class="import-menu-item-icon" />
                  <span>{{ $t("homePage.import.selectFile") }}</span>
                </button>
              </div>
            </span>
          </i18n>
        </div>
        <div v-else class="dropzone-selected">
          <button
            type="button"
            class="dropzone-clear-button"
            :aria-label="$t('homePage.import.clearSelection')"
            @click.stop="clearSelection"
          >
            <IconTrash class="dropzone-clear-icon" />
          </button>
          <IconCheck class="dropzone-check" />
          <span class="dropzone-selected-text">{{ selectedSummary }}</span>
        </div>
        <p v-if="errorMessage" class="dropzone-error">{{ errorMessage }}</p>
      </div>
      <div v-if="isMenuOpen" class="import-menu-backdrop" @click.stop="closeMenu" />

      <p class="import-description">{{ $t("homePage.import.description") }}</p>

      <div v-if="!hideScriptMode" class="script-mode-section">
        <label class="toggle-row">
          <span class="toggle-switch">
            <input type="checkbox" v-model="isScriptMode" />
            <span class="toggle-slider"></span>
          </span>
          <span class="toggle-label">{{ $t("homePage.import.scriptModeLabel") }}</span>
        </label>
        <div class="toggle-description">
          <p class="toggle-description-text">
            {{ $t("novelCreatePage.scriptMode.description") }}
            <button type="button" class="toggle-link" @click="showScriptModeHelp">
              {{ $t("novelCreatePage.scriptMode.learnMore") }}
            </button>
          </p>
        </div>
      </div>

      <button type="button" class="import-button" :disabled="!canImport" @click="onClickImport">
        {{ $t("homePage.import.importButton") }}
      </button>
    </div>
  </div>
</template>

<script lang="ts">
/* eslint-disable no-undef */
import Vue from "vue";
import ScriptModeHelpDialog from "@/components/ui/dialogs/ScriptModeHelpDialog.vue";
import { Dialog } from "@/lib/utils";
import { fileNameToTitle } from "@/lib/utils/fileName";
import { getDataTransferEntries, readFilesFromDirectoryEntry } from "@/lib/utils/directoryEntry";
import { judgeImportSelection, deriveFolderName, MAX_IMPORT_FILE_COUNT } from "@/lib/utils/importSelection";
import IconClose from "~icons/figma/Close";
import IconUpload from "~icons/figma/Upload";
import IconCheck from "~icons/figma/Check";
import IconChevronDown from "~icons/figma/ChevronDown";
import IconFolder from "~icons/figma/Folder";
import IconFile from "~icons/figma/File";
import IconTrash from "~icons/figma/Trash";

export interface ImportDialogResult {
  files: File[];
  title: string;
  isScriptMode: boolean;
}

interface Data {
  selectedFiles: File[];
  folderName: string | null;
  isScriptMode: boolean;
  isDragOver: boolean;
  dragCounter: number;
  isBusy: boolean;
  isMenuOpen: boolean;
  errorMessage: string | null;
}

interface Computed {
  hasSelection: boolean;
  selectedSummary: string;
  canImport: boolean;
}

interface Props {
  hideScriptMode?: boolean;
}

interface Methods {
  onCancel(): void;
  onDropzoneClick(): void;
  toggleMenu(): void;
  closeMenu(): void;
  selectFolder(): void;
  selectFile(): void;
  onFolderInputChange(): void;
  onFileInputChange(): void;
  onDragEnter(): void;
  onDragLeave(): void;
  onDrop(event: DragEvent): Promise<void>;
  applySelection(files: File[], folderName?: string): void;
  clearSelection(): void;
  onClickImport(): void;
  showScriptModeHelp(): void;
}

export default Vue.extend<Data, Methods, Computed, Props>({
  components: { IconClose, IconUpload, IconCheck, IconChevronDown, IconFolder, IconFile, IconTrash },

  props: {
    hideScriptMode: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      selectedFiles: [],
      folderName: null,
      isScriptMode: false,
      isDragOver: false,
      dragCounter: 0,
      isBusy: false,
      isMenuOpen: false,
      errorMessage: null,
    };
  },

  computed: {
    hasSelection() {
      return this.selectedFiles.length > 0;
    },
    selectedSummary() {
      if (this.folderName !== null) {
        return this.$t("homePage.import.selectedFolder", { name: this.folderName }) as string;
      }
      const [first, ...rest] = this.selectedFiles;
      // 表示名も NFC 正規化・拡張子除去した作品名候補（fileNameToTitle）で揃える。
      // これにより実際に採用される作品名（onClickImport 参照）とプレビュー表示が一致する。
      const name = first ? fileNameToTitle(first.name) : "";
      if (rest.length === 0) {
        return this.$t("homePage.import.selectedFile", { name }) as string;
      }
      return this.$t("homePage.import.selectedFileWithOthers", { name, count: rest.length }) as string;
    },
    canImport() {
      return this.hasSelection && !this.isBusy;
    },
  },

  methods: {
    onCancel() {
      this.$close(null);
    },
    onDropzoneClick() {
      // dropzone 自身（アイコン・文言のボックス外側、padding 部分）の直接クリックのみを扱う。
      // ボックス内側は .dropzone-placeholder 側の @click="selectFile" で処理済み（@click.self のため二重発火しない）。
      if (this.isBusy || this.hasSelection) return;
      this.selectFile();
    },
    toggleMenu() {
      this.isMenuOpen = !this.isMenuOpen;
      if (this.isMenuOpen) {
        // メニュー展開直後に先頭項目へフォーカスを移す（キーボード操作対応）。
        this.$nextTick(() => {
          (this.$refs.firstMenuItem as HTMLElement)?.focus();
        });
      }
    },
    closeMenu() {
      this.isMenuOpen = false;
    },
    selectFolder() {
      if (this.isBusy) return;
      this.isMenuOpen = false;
      (this.$refs.folderInput as HTMLInputElement).click();
    },
    selectFile() {
      if (this.isBusy) return;
      this.isMenuOpen = false;
      (this.$refs.fileInput as HTMLInputElement).click();
    },
    onFolderInputChange() {
      const input = this.$refs.folderInput as HTMLInputElement;
      const files = input.files ? Array.from(input.files) : [];
      if (files.length > 0) {
        const first = files[0] as File & { webkitRelativePath?: string };
        const folderName = deriveFolderName(first.webkitRelativePath, first.name);
        this.applySelection(files, folderName);
      }
      input.value = "";
    },
    onFileInputChange() {
      const input = this.$refs.fileInput as HTMLInputElement;
      const files = input.files ? Array.from(input.files) : [];
      if (files.length > 0) {
        this.applySelection(files);
      }
      input.value = "";
    },
    onDragEnter() {
      // dropzone 内の子要素（アイコンやテキスト）をまたぐたびに dragenter/dragleave が
      // 発火するため、カウンターで管理してネスト分の出入りを相殺する。
      this.dragCounter += 1;
      this.isDragOver = true;
    },
    onDragLeave() {
      this.dragCounter = Math.max(0, this.dragCounter - 1);
      if (this.dragCounter === 0) {
        this.isDragOver = false;
      }
    },
    async onDrop(event: DragEvent) {
      this.dragCounter = 0;
      this.isDragOver = false;
      if (this.isBusy) return;
      this.errorMessage = null;

      const items = event.dataTransfer?.items;
      const entries = items ? getDataTransferEntries(items) : [];
      const dirEntries = entries.filter((entry) => entry.isDirectory);

      if (dirEntries.length === 0) {
        const files = event.dataTransfer ? Array.from(event.dataTransfer.files) : [];
        this.applySelection(files);
        return;
      }

      // フォルダは1つだけ受け付ける。複数フォルダやフォルダ＋ファイルの混在は
      // どれを作品名に採用すべきか一意に決められないため拒否する。
      if (dirEntries.length > 1 || entries.length > dirEntries.length) {
        this.errorMessage = this.$t("homePage.import.multipleFolderDropNotSupported") as string;
        return;
      }

      this.isBusy = true;
      try {
        const files = await readFilesFromDirectoryEntry(dirEntries[0] as FileSystemDirectoryEntry);
        this.applySelection(files, dirEntries[0].name);
      } catch (error) {
        console.error(error);
        this.errorMessage = this.$t("homePage.import.folderReadError") as string;
      } finally {
        this.isBusy = false;
      }
    },
    applySelection(files: File[], folderName?: string) {
      const result = judgeImportSelection(files, folderName);

      if (result.status === "error") {
        this.selectedFiles = [];
        this.folderName = null;
        this.errorMessage = this.$t(`homePage.import.${result.errorKey}`, { max: MAX_IMPORT_FILE_COUNT }) as string;
        return;
      }

      this.selectedFiles = result.files;
      this.folderName = result.folderName;
      this.errorMessage = null;
    },
    clearSelection() {
      this.selectedFiles = [];
      this.folderName = null;
      this.errorMessage = null;
      (this.$refs.folderInput as HTMLInputElement).value = "";
      (this.$refs.fileInput as HTMLInputElement).value = "";
    },
    onClickImport() {
      if (!this.canImport) return;
      // folderName は applySelection（judgeImportSelection）で保持時に NFC 正規化済み。
      const title = this.folderName !== null ? this.folderName : fileNameToTitle(this.selectedFiles[0].name);
      const result: ImportDialogResult = {
        files: this.selectedFiles,
        title,
        isScriptMode: this.hideScriptMode ? false : this.isScriptMode,
      };
      this.$close(result);
    },
    showScriptModeHelp() {
      new Dialog(ScriptModeHelpDialog).show();
    },
  },
});
</script>

<style lang="scss" scoped>
@import "@/style-common.scss";

.mask {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  z-index: 9000;
  align-items: center;
  justify-content: center;
  background-color: var(--color-overlay);
}

.import-dialog {
  background-color: var(--color-background-normal);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-lg);
  width: 480px;
  max-width: 90%;
  display: flex;
  flex-direction: column;
  padding: 20px 24px 24px;
  box-sizing: border-box;
}

.import-header {
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  padding-bottom: 16px;
}

.import-header-title {
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-normal);
}

.import-close-button {
  position: absolute;
  right: -8px;
  top: -4px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: var(--radius-xs);
  background: transparent;
  cursor: pointer;
  padding: 0;
  @include hover-overlay;
}

.import-close-icon {
  font-size: var(--font-size-sm);
  color: var(--color-text-normal);
}

.dropzone {
  position: relative;
  box-sizing: border-box;
  min-height: 140px;
  border-radius: var(--radius-md);
  background-color: var(--color-surface-muted);
  border: 1px solid transparent;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 16px;

  &[data-dragover="true"] {
    border-width: 3px;
    border-color: var(--color-primary);
    background-color: var(--color-surface-base);
  }

  &[data-error="true"] {
    border-width: 3px;
    border-color: var(--color-warning);
  }
}

.dropzone-input {
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
  overflow: hidden;
  pointer-events: none;
}

.dropzone-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  flex: 1;
  gap: 6px;
  text-align: center;

  &.is-clickable {
    cursor: pointer;
  }
}

.dropzone-icon {
  font-size: var(--font-size-xxl);
  color: var(--color-text-normal);
  margin-bottom: 2px;
}

.dropzone-label {
  margin: 0;
  font-size: var(--font-size-sm);
  color: var(--color-text-normal);
}

.dropzone-selected {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  text-align: center;
}

.dropzone-check {
  font-size: var(--font-size-xl);
  color: var(--color-success);
}

.dropzone-selected-text {
  font-size: var(--font-size-sm);
  color: var(--color-text-normal);
  word-break: break-all;
}

.dropzone-clear-button {
  position: absolute;
  top: 8px;
  right: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: var(--radius-xs);
  background: transparent;
  cursor: pointer;
  padding: 0;
  @include hover-overlay;
}

.dropzone-clear-icon {
  font-size: var(--font-size-sm);
  color: var(--color-warning);
}

.import-trigger-wrap {
  position: relative;
  display: inline-block;
}

.import-trigger {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  border: none;
  background: none;
  padding: 0;
  font-size: inherit;
  font-weight: var(--font-weight-bold);
  color: var(--color-primary);
  cursor: pointer;
}

.import-trigger-icon {
  font-size: var(--font-size-xs);
}

.import-menu {
  position: absolute;
  top: calc(100% + 4px);
  left: 50%;
  transform: translateX(-50%);
  min-width: 160px;
  background-color: var(--color-background-normal);
  border-radius: var(--radius-sm);
  box-shadow: var(--shadow-md);
  padding: 4px 0;
  z-index: 10;
  white-space: nowrap;
}

.import-menu-item {
  position: relative;
  @include hover-overlay;
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 10px 16px;
  border: none;
  background: transparent;
  color: var(--color-text-normal);
  cursor: pointer;
  font-size: var(--font-size-xs);
}

.import-menu-item-icon {
  font-size: var(--font-size-lg);
  flex-shrink: 0;
}

.import-menu-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
}

.import-description {
  margin: 12px 0 0;
  font-size: var(--font-size-xs);
  line-height: var(--line-height-default);
  color: var(--color-text-normal);
}

.dropzone-error {
  margin: 4px 0 0;
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-bold);
  color: var(--color-warning);
}

// script mode toggle
.script-mode-section {
  width: 100%;
  margin-top: 28px;
}

.toggle-row {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  user-select: none;
}

.toggle-switch {
  position: relative;
  display: inline-block;
  width: 56px;
  height: 32px;
  flex-shrink: 0;

  input {
    opacity: 0;
    width: 0;
    height: 0;
  }

  .toggle-slider {
    position: absolute;
    inset: 0;
    background-color: var(--color-inactive);
    border-radius: 32px;
    transition: background-color 0.25s;

    &::before {
      content: "";
      position: absolute;
      width: 26px;
      height: 26px;
      left: 3px;
      bottom: 3px;
      background-color: var(--color-background-normal);
      border-radius: 50%;
      transition: transform 0.25s;
    }
  }

  input:checked + .toggle-slider {
    background-color: var(--color-primary);
  }

  input:checked + .toggle-slider::before {
    transform: translateX(24px);
  }
}

.toggle-label {
  font-size: var(--font-size-md);
  color: var(--color-text-normal);
}

.toggle-description {
  margin: 6px 0 0 0;
  font-size: var(--font-size-xs);
  line-height: var(--line-height-default);
}

.toggle-description-text {
  margin: 0;
  color: var(--color-text-normal);
}

.toggle-link {
  display: inline;
  background: none;
  border: none;
  padding: 0;
  font-size: inherit;
  color: var(--color-primary);
  text-decoration: underline;
  cursor: pointer;

  &:hover {
    opacity: 0.8;
  }
}

.import-button {
  width: 100%;
  margin-top: 28px;
  padding: 10px;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-on-fixed-bg);
  background-color: var(--color-primary);
  border: none;
  border-radius: var(--radius-rounded);
  cursor: pointer;
  transition: opacity 0.15s;

  &:hover:not(:disabled) {
    opacity: 0.85;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
}
</style>
